'use client';

import { useEffect, useState } from 'react';
import { track } from './events';

/**
 * Browser confirmation dialog when the user tries to navigate away
 * mid-call. Non-customisable text on every browser since 2017 but
 * the dialog still fires.
 */
export function useBeforeUnloadGuard(active: boolean): void {
  useEffect(() => {
    if (!active || typeof window === 'undefined') return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Required by some legacy browsers; modern Chromium/Firefox/Safari
      // ignore the string and show their own copy.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [active]);
}

/**
 * Telemetry-only signal — emits tab_hidden / tab_visible while a call
 * is active. The SDK manages the actual WebSocket lifecycle; we just
 * want to know how often users background the tab so we can size the
 * heartbeat reconciliation logic.
 */
export function useTabVisibilityTelemetry(active: boolean): void {
  useEffect(() => {
    if (!active || typeof document === 'undefined') return;
    const handler = () => {
      track(document.hidden ? 'tab_hidden' : 'tab_visible');
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [active]);
}

/**
 * Surfaces navigator.onLine to the UI and emits network_offline /
 * network_online events. Heartbeat retries silently anyway, but a
 * visible "you are offline" hint is gentler than a frozen UI.
 */
export function useNetworkStatus(): boolean {
  const [online, setOnline] = useState<boolean>(true);

  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    setOnline(navigator.onLine);
    const onOnline = () => {
      setOnline(true);
      track('network_online');
    };
    const onOffline = () => {
      setOnline(false);
      track('network_offline');
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  return online;
}
